// The Stores filter panel: a port of MapFilterSheet.swift as a right-hand drawer. Like the iOS sheet it edits a draft
// and shows the live result count on the Apply button, so a manager can try criteria without the table jumping
// under them; Apply writes the filter to the URL (listState.ts), Cancel or Escape drops the draft.
//
// Sections follow the sheet: quick filters, record type, pipeline and stage, last visit result, priority, owner
// (managers, as iOS), territory, days since last visit / activity, tags, then the toggles. Two web additions from
// WEB_PLAN.md §5.5: "Whole pipeline" (stores on a board, any stage) and the "Unassigned" owner.

import { RotateCcw } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { PRIORITIES, PRIORITY_COLORS, PRIORITY_TITLES, RECORD_TYPES, RECORD_TYPE_PLURALS, USER_ROLE_TITLES } from '@/contracts/enums';
import { orderedStages, type PipelinePayload, type RecordPayload } from '@/contracts/models';
import { usePipelines, useSettings, useTerritories, useViewer, useVisibleUsers, useVisitResults } from '@/data/hooks';
import { formatGrouped } from '@/domain/format';
import {
  EMPTY_FILTER,
  FILTER_PRESETS,
  FILTER_PRESET_COLORS,
  FILTER_PRESET_TITLES,
  RECORD_TYPE_PIN_COLORS,
  allTags,
  applyFilter,
  isFilterActive,
  isPresetOn,
  togglePreset,
  type FilterContext,
  type MapFilter,
} from '@/domain/records';
import { pickerUsers } from '@/domain/team';
import { recordsInTerritory } from '@/domain/territories';
import { fullName } from '@/domain/util';
import { Button, Checkbox, Chip, ColorDot, Dialog, Field, Input, Switch, cn, useLatest } from '@/ui';
import {
  DAYS_PRESETS,
  daysRange,
  filterableTerritories,
  hasIn,
  isDaysPresetOn,
  parseDayBound,
  toggleIn,
  withDaysRange,
  type DaysRangeKind,
} from './filterModel';

export interface FilterPanelProps {
  open: boolean;
  onClose: () => void;
  /** The applied filter (the draft starts from it each time the panel opens). */
  filter: MapFilter;
  onApply: (filter: MapFilter) => void;
  /** The stores the filter runs over (visible; archived ones when the toggle is on): live count, tags, territory counts. */
  records: readonly RecordPayload[];
  filterContext: FilterContext;
  /** Managers and admins also filter by owner (MapFilterSheet shows that section to managers only). */
  isManager: boolean;
}

export function FilterPanel(props: FilterPanelProps) {
  // Mounted only while open, so every opening starts a fresh draft from the applied filter.
  return props.open ? <FilterDialog {...props} /> : null;
}

function FilterDialog({ onClose, filter, onApply, records, filterContext, isManager }: FilterPanelProps) {
  const [draft, setDraft] = useState<MapFilter>(filter);
  const [invalidBounds, setInvalidBounds] = useState<ReadonlySet<string>>(new Set());
  // Bumped by Reset: the day fields start over from the draft even when its bounds did not change (a typo typed into
  // an empty "At least" leaves the bound unset, so only a remount clears the text and the error with it).
  const [resetCount, setResetCount] = useState(0);
  const visitResults = useVisitResults();
  const orderedResults = useMemo(() => [...visitResults.values].sort((a, b) => a.order - b.order), [visitResults]);

  const count = useMemo(() => applyFilter(records, draft, filterContext).length, [records, draft, filterContext]);
  const active = isFilterActive(draft);

  const setInvalid = (key: string, invalid: boolean) =>
    setInvalidBounds((prev) => {
      if (prev.has(key) === invalid) return prev;
      const next = new Set(prev);
      if (invalid) next.add(key);
      else next.delete(key);
      return next;
    });

  const apply = () => {
    onApply(draft);
    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title="Filters"
      placement="right"
      size="md"
      footer={
        <div className="flex w-full flex-wrap items-center gap-2">
          <Button
            variant="ghost"
            icon={<RotateCcw />}
            onClick={() => {
              setDraft({ ...EMPTY_FILTER });
              setResetCount((n) => n + 1);
            }}
            disabled={!active && invalidBounds.size === 0}
          >
            Reset
          </Button>
          <span className="flex-1" />
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={apply} disabled={invalidBounds.size > 0}>
            Show {formatGrouped(count)} result{count === 1 ? '' : 's'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        <Section title="Quick filters" hint="Shortcuts that set the criteria below. Click again to clear.">
          <ChipRow>
            {FILTER_PRESETS.map((p) => (
              <Chip
                key={p}
                color={FILTER_PRESET_COLORS[p]}
                selected={isPresetOn(draft, p, visitResults.values)}
                onClick={() => setDraft((f) => togglePreset(f, p, visitResults.values))}
                size="md"
              >
                {FILTER_PRESET_TITLES[p]}
              </Chip>
            ))}
          </ChipRow>
        </Section>

        <Section title="Record type">
          <ChipRow>
            {RECORD_TYPES.map((t) => (
              <Chip
                key={t}
                color={RECORD_TYPE_PIN_COLORS[t]}
                selected={draft.types.includes(t)}
                onClick={() => setDraft((f) => ({ ...f, types: toggleIn(f.types, t) }))}
                size="md"
              >
                {RECORD_TYPE_PLURALS[t]}
              </Chip>
            ))}
          </ChipRow>
        </Section>

        <PipelineSection draft={draft} setDraft={setDraft} />

        <Section title="Last visit result">
          {orderedResults.length === 0 ? (
            <Empty>No visit results configured</Empty>
          ) : (
            <ChipRow>
              {orderedResults.map((r) => (
                <Chip
                  key={r.id}
                  color={r.colorHex}
                  selected={hasIn(draft.visitResultIds, r.id, true)}
                  onClick={() => setDraft((f) => ({ ...f, visitResultIds: toggleIn(f.visitResultIds, r.id, true) }))}
                  size="md"
                >
                  {r.name || 'Untitled result'}
                </Chip>
              ))}
            </ChipRow>
          )}
        </Section>

        <Section title="Priority">
          <div className="grid grid-cols-5 gap-2">
            {PRIORITIES.map((p) => (
              <Chip
                key={p}
                color={PRIORITY_COLORS[p]}
                selected={draft.priorities.includes(p)}
                onClick={() => setDraft((f) => ({ ...f, priorities: toggleIn(f.priorities, p) }))}
                title={PRIORITY_TITLES[p]}
                size="md"
                className="w-full justify-center"
              >
                <span aria-hidden>{p}</span>
                <span className="sr-only">{PRIORITY_TITLES[p]}</span>
              </Chip>
            ))}
          </div>
        </Section>

        {isManager && <OwnerSection draft={draft} setDraft={setDraft} />}

        <TerritorySection draft={draft} setDraft={setDraft} records={records} />

        <DaysSection
          kind="visit"
          resetCount={resetCount}
          title="Days since last visit"
          hint='Stores that were never visited are excluded when a range is set. Use "Never visited" below to find them.'
          draft={draft}
          setDraft={setDraft}
          onInvalid={setInvalid}
        />
        <DaysSection
          kind="activity"
          resetCount={resetCount}
          title="Days since last activity"
          hint='Counts visits, calls, texts, emails, notes and tasks. Stores with no activity at all match an "at least" bound.'
          draft={draft}
          setDraft={setDraft}
          onInvalid={setInvalid}
        />

        <TagsSection draft={draft} setDraft={setDraft} records={records} />

        <Section title="More">
          <div className="flex flex-col gap-3">
            <Switch label="Only my stores" checked={draft.onlyMine} onCheckedChange={(v) => setDraft((f) => ({ ...f, onlyMine: v }))} />
            <Switch label="Never visited" checked={draft.neverVisited} onCheckedChange={(v) => setDraft((f) => ({ ...f, neverVisited: v }))} />
            <Switch
              label="Has a scheduled activity"
              checked={draft.hasScheduledActivity}
              onCheckedChange={(v) => setDraft((f) => ({ ...f, hasScheduledActivity: v }))}
            />
            <Switch
              label="Follow-up due by today"
              checked={draft.followUpDue}
              onCheckedChange={(v) => setDraft((f) => ({ ...f, followUpDue: v }))}
            />
          </div>
        </Section>
      </div>
    </Dialog>
  );
}

type SetDraft = (update: (f: MapFilter) => MapFilter) => void;

function Section({ title, hint, children }: { title: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <fieldset className="flex min-w-0 flex-col gap-2.5">
      <legend className="mb-2.5 text-xs font-semibold tracking-[0.08em] text-fg-muted uppercase">{title}</legend>
      {children}
      {hint && <p className="text-xs text-fg-muted">{hint}</p>}
    </fieldset>
  );
}

function ChipRow({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap gap-2">{children}</div>;
}

function Empty({ children }: { children: ReactNode }) {
  return <p className="text-xs text-fg-muted">{children}</p>;
}

/** A check row with a color dot, a title and a caption (MapFilterSheet.checkRow). */
function CheckRow({
  checked,
  onChange,
  color,
  title,
  subtitle,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  color?: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <Checkbox
      checked={checked}
      onCheckedChange={onChange}
      label={
        <span className="inline-flex items-center gap-2">
          {color && <ColorDot color={color} size={10} />}
          {title}
        </span>
      }
      description={subtitle}
      className="py-1"
    />
  );
}

function ScrollList({ children, label }: { children: ReactNode; label: string }) {
  return (
    <div role="group" aria-label={label} className="flex max-h-64 flex-col overflow-y-auto rounded-control border border-line px-3 py-1.5 gt-scroll">
      {children}
    </div>
  );
}

function PipelineSection({ draft, setDraft }: { draft: MapFilter; setDraft: SetDraft }) {
  const pipelines = usePipelines();
  const sorted = useMemo(
    () =>
      [...pipelines.values].sort(
        (a: PipelinePayload, b: PipelinePayload) =>
          RECORD_TYPES.indexOf(a.recordType) - RECORD_TYPES.indexOf(b.recordType) ||
          Number(b.isDefault) - Number(a.isDefault) ||
          a.name.localeCompare(b.name),
      ),
    [pipelines],
  );
  return (
    <Section title="Pipeline and stage">
      {sorted.length === 0 && <Empty>No pipelines configured</Empty>}
      {sorted.map((p) => (
        <div key={p.id} className="flex flex-col gap-2 rounded-control border border-line p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="min-w-0 text-sm font-semibold text-fg">
              {p.name || 'Untitled pipeline'}
              <span className="ml-1.5 text-xs font-normal text-fg-muted">{RECORD_TYPE_PLURALS[p.recordType]}</span>
            </span>
            <Chip
              color="#102A5E"
              selected={hasIn(draft.pipelineIds, p.id, true)}
              onClick={() => setDraft((f) => ({ ...f, pipelineIds: toggleIn(f.pipelineIds, p.id, true) }))}
              title={`Stores on ${p.name || 'this pipeline'}, any stage`}
            >
              Whole pipeline
            </Chip>
          </div>
          <ChipRow>
            {orderedStages(p).map((s) => (
              <Chip
                key={s.id}
                color={s.colorHex}
                selected={hasIn(draft.stageIds, s.id, true)}
                onClick={() => setDraft((f) => ({ ...f, stageIds: toggleIn(f.stageIds, s.id, true) }))}
              >
                {s.name || 'Untitled stage'}
              </Chip>
            ))}
          </ChipRow>
        </div>
      ))}
    </Section>
  );
}

function OwnerSection({ draft, setDraft }: { draft: MapFilter; setDraft: SetDraft }) {
  const users = useVisibleUsers();
  const owners = useMemo(() => pickerUsers(users.values), [users]);
  return (
    <Section title="Owner">
      <ScrollList label="Owners">
        <CheckRow
          checked={draft.includeUnassigned}
          onChange={(v) => setDraft((f) => ({ ...f, includeUnassigned: v }))}
          color="#8C919E"
          title="Unassigned"
          subtitle="Stores with no owner"
        />
        {owners.map((u) => (
          <CheckRow
            key={u.id}
            checked={hasIn(draft.ownerIds, u.id, true)}
            onChange={() => setDraft((f) => ({ ...f, ownerIds: toggleIn(f.ownerIds, u.id, true) }))}
            color={u.colorHex}
            title={fullName(u) || 'Unnamed'}
            subtitle={USER_ROLE_TITLES[u.role]}
          />
        ))}
      </ScrollList>
    </Section>
  );
}

function TerritorySection({ draft, setDraft, records }: { draft: MapFilter; setDraft: SetDraft; records: readonly RecordPayload[] }) {
  const territories = useTerritories();
  const viewer = useViewer();
  const settings = useSettings();
  const list = useMemo(
    () => filterableTerritories(territories.values, viewer, settings),
    [territories, viewer, settings],
  );
  // `records(in:)` per territory (tagged or inside the shape), counted once per opening.
  const counts = useMemo(() => new Map(list.map((t) => [t.id, recordsInTerritory(t, records).length])), [list, records]);
  return (
    <Section title="Territory">
      {list.length === 0 ? (
        <Empty>No territories yet</Empty>
      ) : (
        <ScrollList label="Territories">
          {list.map((t) => {
            const n = counts.get(t.id) ?? 0;
            return (
              <CheckRow
                key={t.id}
                checked={hasIn(draft.territoryIds, t.id, true)}
                onChange={() => setDraft((f) => ({ ...f, territoryIds: toggleIn(f.territoryIds, t.id, true) }))}
                color={t.colorHex}
                title={`${t.name || 'Untitled territory'}${t.isActive ? '' : ' (inactive)'}`}
                subtitle={`${formatGrouped(n)} store${n === 1 ? '' : 's'}`}
              />
            );
          })}
        </ScrollList>
      )}
    </Section>
  );
}

function DaysSection({
  kind,
  resetCount,
  title,
  hint,
  draft,
  setDraft,
  onInvalid,
}: {
  kind: DaysRangeKind;
  resetCount: number;
  title: string;
  hint: string;
  draft: MapFilter;
  setDraft: SetDraft;
  onInvalid: (key: string, invalid: boolean) => void;
}) {
  const range = daysRange(draft, kind);
  // A range chip sets both bounds; the fields remount to show exactly that, replacing any invalid text left in them
  // ("Any" on a field holding "abc" would otherwise change nothing and keep Apply disabled).
  const [chipCount, setChipCount] = useState(0);
  const fieldKey = `${resetCount}-${chipCount}`;
  return (
    <Section title={title} hint={hint}>
      <div role="group" aria-label={`${title}: ranges`} className="flex flex-wrap gap-2">
        {DAYS_PRESETS.map((p) => (
          <Chip
            key={p.id}
            selected={isDaysPresetOn(draft, kind, p)}
            onClick={() => {
              setDraft((f) => withDaysRange(f, kind, p.min, p.max));
              setChipCount((n) => n + 1);
            }}
            size="md"
            title={p.id === 'any' ? 'Any' : `${p.title} days`}
          >
            {p.title}
          </Chip>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <DayBoundField
          key={`min-${fieldKey}`}
          label="At least"
          value={range.min}
          onChange={(v) => setDraft((f) => withDaysRange(f, kind, v, daysRange(f, kind).max))}
          onInvalid={(bad) => onInvalid(`${kind}-min`, bad)}
        />
        <DayBoundField
          key={`max-${fieldKey}`}
          label="At most"
          value={range.max}
          onChange={(v) => setDraft((f) => withDaysRange(f, kind, daysRange(f, kind).min, v))}
          onInvalid={(bad) => onInvalid(`${kind}-max`, bad)}
        />
      </div>
      {range.min !== undefined && range.max !== undefined && range.min > range.max && (
        <p className="text-xs text-warning-text">"At least" is more than "At most", so no store can match.</p>
      )}
    </Section>
  );
}

/** "At least" / "At most" in days: typing keeps its own text; valid values go to the draft, empty means Any. */
function DayBoundField({
  label,
  value,
  onChange,
  onInvalid,
}: {
  label: string;
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  onInvalid: (invalid: boolean) => void;
}) {
  const [text, setText] = useState(value === undefined ? '' : String(value));
  // A chip click changes the bound from outside: show it unless the field already says the same thing.
  useEffect(() => {
    setText((t) => (parseDayBound(t) === value ? t : value === undefined ? '' : String(value)));
  }, [value]);
  const parsed = parseDayBound(text);
  const invalid = parsed === 'invalid';
  // The callback is a new closure each render; read it through a ref so the effect runs only when validity flips.
  const report = useLatest(onInvalid);
  useEffect(() => {
    report.current(invalid);
    // Clear the flag when the field goes away (panel closed with a bad value).
    return () => report.current(false);
  }, [invalid, report]);
  return (
    <Field label={label} error={invalid ? 'Whole days, 0 to 3650' : undefined}>
      <Input
        inputMode="numeric"
        placeholder="Any"
        value={text}
        trailing={<span className="text-xs text-fg-muted">days</span>}
        onChange={(e) => {
          const next = e.target.value;
          setText(next);
          const v = parseDayBound(next);
          if (v !== 'invalid') onChange(v);
        }}
        className={cn('tabular-nums')}
      />
    </Field>
  );
}

function TagsSection({ draft, setDraft, records }: { draft: MapFilter; setDraft: SetDraft; records: readonly RecordPayload[] }) {
  const tags = useMemo(() => allTags(records), [records]);
  // iOS hides the section when no store has a tag; keep tags the filter already uses visible even then.
  const shown = useMemo(() => [...tags, ...draft.tags.filter((t) => !tags.includes(t))], [tags, draft.tags]);
  if (shown.length === 0) return null;
  return (
    <Section title="Tags" hint="Stores with any of the chosen tags.">
      <div className="flex max-h-48 flex-wrap gap-2 overflow-y-auto gt-scroll">
        {shown.map((t) => (
          <Chip
            key={t}
            color="#009E9E"
            selected={draft.tags.includes(t)}
            onClick={() => setDraft((f) => ({ ...f, tags: toggleIn(f.tags, t) }))}
          >
            {t}
          </Chip>
        ))}
      </div>
    </Section>
  );
}
